import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { getAllBrokerRatesList } from '../../../setup/actions/brokerrate.actions';
import { SelectionItem } from '../../../../api/core/core.types';
import Select from '../../../../components/forms/Select';
import { getDeskList } from '../../../setup/actions/desk.actions';
import { getClassifierList } from '../../../admin/actions/classifier.actions';
import { updateRatesRequestTestFilters } from '../../actions/ratesrequesttest.actions';
import DatePicker from '../../../../components/forms/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { SelectedOption } from '../../../../api/api.types';
import { dateOrderCorrect, dateTimeOrderCorrect, populateTimeSelect } from '../../../../helpers/date.helper';
import { RatesRequestTestParameters } from '../../../../api/pricing/pricing.types';
import api from '../../../../api/pricing/pricing.client';
import XMLViewer from 'react-xml-viewer';
import { Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';

interface InvalidFields {
  dates: boolean;
  times: boolean;
  brokerRate: boolean;
  pickupLoc: boolean;
  dropoffLoc: boolean;
  residency: boolean;
}

export const RatesRequestTest: React.FC<any> = () => {
  const brokerRates = useAppSelector((state) => state.setup.brokerRates.brokerRates);
  const desks = useAppSelector((state) => state.setup.desks.desks);
  const classifiers = useAppSelector((state) => state.admin.classifiers.classifiers);
  const ratesRequestTestFilters = useAppSelector((state) => state.rates.ratesRequestTest.filters);
  const [ brokerRate, setBrokerRate ] = useState<any>();
  const [ pickupLocation, setPickupLocation ] = useState<any>();
  const [ dropoffLocation, setDropoffLocation ] = useState<any>();
  const [ pickupDate, setPickupDate ] = useState<Dayjs | undefined>(dayjs().add(2, 'days'));
  const [ dropoffDate, setDropoffDate ] = useState<Dayjs | undefined>(dayjs().add(5, 'days'));
  const [ pickupTime, setPickupTime ] = useState<{ value: string, label: string }>(
    { value: '11:00', label: '11:00' });
  const [ dropoffTime, setDropoffTime ] = useState<{ value: string, label: string }>(
    { value: '11:00', label: '11:00' });
  const [ filtersSubmitted, setFiltersSubmitted ] = useState(false);
  const [ driverAge, setDriverAge ] = useState<number>(30);
  const [ residency, setResidency ] = useState<any>();
  const [ promoCode, setPromoCode ] = useState<string>('');
  const [ loading, setLoading ] = useState(false);
  const [ invalidFields, setInvalidFields ] = useState<InvalidFields>({
    dates: false,
    times: false,
    brokerRate: true,
    pickupLoc: true,
    dropoffLoc: true,
    residency: false
  });
  const [ responseXMLData, setResponseXMLData ] = useState('');
  const [ failedRequest, setFailedReques ] = useState(false);
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getAllBrokerRatesList());
    dispatch(getDeskList());
    dispatch(getClassifierList({ classifierTypeCode: 'COUNTRY' }));
  }, [ dispatch ]);

  useEffect(() => {
    const gbClassifierVal = getSortedItems(classifiers).find((itm) => itm.value === 'GB');
    if (gbClassifierVal && !residency) {
      setResidency(gbClassifierVal);
    }
  }, [ classifiers, residency ]);

  const handleUpdateFilterValuesFromState = useCallback((filters: RatesRequestTestParameters) => {
    const pickupD = dayjs(filters.pickupDateTime.split('T')[ 0 ]);
    const dropoffD = dayjs(filters.dropoffDateTime.split('T')[ 0 ]);
    const pickupT = filters.pickupDateTime.split('T')[ 1 ].slice(0, -3);
    const dropoffT = filters.dropoffDateTime.split('T')[ 1 ].slice(0, -3);
    const age = filters.age;
    const pickupDesk = getSortedItems(desks).find((itm) => itm.value === filters.pickupLocation);
    const dropoffDesk = getSortedItems(desks).find((itm) => itm.value === filters.dropoffLocation);
    const selResidency = getSortedItems(classifiers).find((itm) => itm.value === filters.residency);
    const selBrokerRate = getSortedItems(brokerRates).find((itm) => itm.value === filters.brokerRate);

    setPickupDate(pickupD);
    if (pickupDesk) {
      setPickupLocation(pickupDesk);
    }
    setDropoffDate(dropoffD);
    if (dropoffDesk) {
      setDropoffLocation(dropoffDesk);
    }
    setPickupTime({ value: pickupT, label: pickupT });
    setDropoffTime({ value: dropoffT, label: dropoffT });
    setDriverAge(age);
    if (selResidency) {
      setResidency(selResidency);
    }
    if (selBrokerRate) {
      setBrokerRate(selBrokerRate);
    }
    setPromoCode(filters.promoCode);
    setInvalidFields({
      dates: false,
      times: false,
      brokerRate: !selBrokerRate,
      pickupLoc: !pickupDesk,
      dropoffLoc: !dropoffDesk,
      residency: !selResidency
    });
  }, [ desks, brokerRates, classifiers ]);

  useEffect(() => {
    if (ratesRequestTestFilters) {
      handleUpdateFilterValuesFromState(ratesRequestTestFilters);
    }
  }, [ ratesRequestTestFilters, handleUpdateFilterValuesFromState ]);

  const getSortedItems = (items: any[]) => {
    const brokerRateOptions: SelectionItem[] = [];

    items.forEach((br) => {
      brokerRateOptions.push({ value: br.code, label: `${br.code} (${br.name})` });
    });

    return brokerRateOptions.sort((a, b) => ((a.label < b.label) ? -1 :
      (a.label > b.label) ? 1 : 0));
  };

  const checkIfInvalidFields = () => {
    for (const key of Object.keys(invalidFields)) {
      if (invalidFields[ key as keyof InvalidFields ]) {
        return true;
      }
    }
    return false;
  };

  const makeRequest = async (e: FormEvent) => {
    setFailedReques(false);
    setResponseXMLData('');
    e.preventDefault();

    setFiltersSubmitted(true);
    let invalid = false;
    if (checkIfInvalidFields()) {
      invalid = true;
    }
    const resValidityCheck = checkTimesAndDatesValid({ startDate: pickupDate, endDate: dropoffDate });
    if (resValidityCheck.invalid) {
      invalid = true;
    }
    if (invalid) {
      return;
    }
    const startDateTime = resValidityCheck.startDateTime + ':00';
    const endDateTime = resValidityCheck.endDateTime + ':00';

    const request: RatesRequestTestParameters = {
      brokerRate: brokerRate!.value,
      pickupLocation: pickupLocation!.value,
      dropoffLocation: dropoffLocation!.value,
      residency: residency!.value,
      age: driverAge,
      promoCode: promoCode,
      pickupDateTime: startDateTime,
      dropoffDateTime: endDateTime
    };

    dispatch(updateRatesRequestTestFilters(request));

    setLoading(true);
    try {
      const res = await api.RatesRequestTest.makeRatesRequestTest(request);
      setResponseXMLData(res.data);
      setLoading(false);
    } catch (er) {
      setLoading(false);
      setFailedReques(true);
    }
  };

  const patchTimeField = (option: SelectedOption, field: string) => {
    let error;
    if (field === 'pickupTime') {
      error = dateTimeOrderCorrect({
        startDate: pickupDate,
        endDate: dropoffDate, startTime: option.value,
        endTime: dropoffTime.value
      }).error;
      setPickupTime(option);
    } else {
      error = dateTimeOrderCorrect({
        startDate: pickupDate,
        endDate: dropoffDate, startTime: pickupTime.value,
        endTime: option.value
      }).error;
      setDropoffTime(option);
    }

    setInvalidFields({
      ...invalidFields,
      times: error
    });
  };

  const patchDateField = (field: string, date?: Dayjs) => {
    let startDate = pickupDate;
    let endDate = dropoffDate;
    if (field === 'pickupDate') {
      startDate = date;
      setPickupDate(date);
    } else {
      endDate = date;
      setDropoffDate(date);
    }

    checkTimesAndDatesValid({ startDate: startDate, endDate: endDate });
  };

  const checkTimesAndDatesValid = (dateRange: { startDate?: Dayjs, endDate?: Dayjs }) => {
    const resDateCheck = dateOrderCorrect({ startDate: dateRange.startDate, endDate: dateRange.endDate },
      undefined, undefined, true);
    const resDateTimeCheck = dateTimeOrderCorrect({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate, startTime: pickupTime.value, endTime: dropoffTime.value
    });

    setInvalidFields({
      ...invalidFields,
      dates: !resDateCheck,
      times: resDateTimeCheck.error
    });

    return {
      invalid: !resDateCheck || resDateTimeCheck.error,
      startDateTime: resDateTimeCheck.startDateTime,
      endDateTime: resDateTimeCheck.endDateTime
    };
  };

  const openXMLInfoLink = () => {
    window.open(t('b.rates_request_test.xlm_info_link_address', { interpolation: { escapeValue: false } }),
      '_blank')!.focus();
  };

  return (
    <main>
      <div className="page-content rates-request-test-view">
        <div className="d-flex justify-content-between align-items-center">
          <h1>{t('page_title.rates_request_test')}</h1>
        </div>
        <form className="container-fluid filter-container form-group form-inline d-flex justify-content-between">
          <div className="d-flex row flex-wrap filters-container ml-1 w-100">
            <div className="d-flex col-6 input-element-container flex-wrap flex-row left-container">
              <div className="d-flex cells-row pt-1">
                <div className="row-label-container">
                  {t('l.rates_request_test.broker_rate')}
                </div>
                <Select
                  value={brokerRate}
                  className={`mr-1 ${invalidFields.brokerRate && filtersSubmitted ? 'invalid' : ''}`}
                  placeholder={t('ph.rates_request_test.by_broker_rate')}
                  elWidth={280}
                  zIndex={10000}
                  disableClear
                  options={getSortedItems(brokerRates)}
                  onChange={(v: any) => {
                    setInvalidFields({
                      ...invalidFields,
                      brokerRate: !v?.hasOwnProperty('value') && !v?.length
                    });
                    setBrokerRate(v);
                  }}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  label="broker"
                />
                <div className="date-picker-container">
                </div>
                <div className="rates-request-test-datepicker d-flex align-items-start
                                time-input-container age-input-container">
                  <label
                    className="pr-1 pt-1 age-label-container">{t('l.rates_request_test.driver_age')}</label>
                  <input type="number" value={driverAge} defaultValue={30} onChange={
                    (v) => setDriverAge(Number(v.target.value))}
                         min={0}
                         className="form-control col margins"/>
                </div>
              </div>
              <div className="d-flex cells-row">
                <div className="row-label-container">
                  {t('l.rates_request_test.pickup')}
                </div>
                <Select
                  value={pickupLocation}
                  className={`mr-1 ${filtersSubmitted && invalidFields.pickupLoc ? 'invalid' : ''}`}
                  placeholder={t('ph.rates_request_test.by_pickup_location')}
                  elWidth={280}
                  zIndex={9999}
                  disableClear
                  options={getSortedItems(desks)}
                  onChange={(v: any) => {
                    setInvalidFields({
                      ...invalidFields,
                      pickupLoc: !v?.hasOwnProperty('value') && !v?.length
                    });
                    setPickupLocation(v);
                  }}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  label="pickup location"
                />
                <div className="date-picker-container d-flex align-items-end">
                  <DatePicker
                    id="pickup_date"
                    isEdit
                    isFilter
                    isInvalid={invalidFields.dates && filtersSubmitted}
                    date={pickupDate ? pickupDate : undefined}
                    onChange={(val) => {
                      patchDateField('pickupDate', val);
                    }}
                  />
                </div>
                <div
                  className="third-column-container d-flex align-items-end"
                >
                  <Select
                    className={`mr-1 ${invalidFields.times && filtersSubmitted ? 'invalid' : ''}`}
                    value={pickupTime}
                    disableMinWidth
                    disableClear
                    placeholder="Choose pickup time"
                    options={(populateTimeSelect())}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    label="pickup time"
                    onChange={(value) =>
                      patchTimeField(value as SelectedOption, 'pickupTime')}
                  />
                </div>
              </div>
              <div className="d-flex">
                <div className="row-label-container">
                  {t('l.rates_request_test.dropoff')}
                </div>
                <Select
                  value={dropoffLocation}
                  className={`mr-1 ${invalidFields.dropoffLoc && filtersSubmitted ? 'invalid' : ''}`}
                  placeholder={t('ph.rates_request_test.by_dropoff_location')}
                  elWidth={280}
                  disableClear
                  options={getSortedItems(desks)}
                  onChange={(v: any) => {
                    setInvalidFields({
                      ...invalidFields,
                      dropoffLoc: !v?.hasOwnProperty('value') && !v?.length
                    });
                    setDropoffLocation(v);
                  }}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  label="dropoff location"
                />
                <div className="date-picker-container d-flex align-items-end">
                  <DatePicker
                    id="dropoff_date"
                    isEdit
                    isFilter
                    isInvalid={invalidFields.dates && filtersSubmitted}
                    date={dropoffDate ? dropoffDate : undefined}
                    onChange={(val) => patchDateField('dropoffDate', val)}
                  />
                </div>
                <div
                  className="third-column-container d-flex align-items-end"
                >
                  <Select
                    value={dropoffTime}
                    className={`mr-1 ${invalidFields.times && filtersSubmitted ? 'invalid' : ''}`}
                    placeholder="Choose dropoff time"
                    options={(populateTimeSelect())}
                    disableMinWidth
                    disableClear
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    label="dropoff time"
                    onChange={(value) =>
                      patchTimeField(value as SelectedOption, 'dropoffToTime')}
                  />
                </div>
              </div>
            </div>
            <div className="d-flex col-6 input-element-container">
              <div className="right-container d-flex pt-1 flex-column">
                <div className="d-flex flex-row cells-row">
                  <div className="d-flex flex-row cell-with-label mr-3">
                    <label className="pr-1">
                      {t('l.rates_request_test.source_country')}
                    </label>
                    <Select
                      value={residency}
                      className={`mr-1 ${invalidFields.residency && filtersSubmitted ? 'invalid' : ''}`}
                      placeholder={t('ph.rates_request_test.by_residency')}
                      elWidth={100}
                      disableClear
                      options={getSortedItems(classifiers)}
                      onChange={(v: any) => {
                        setInvalidFields({
                          ...invalidFields,
                          residency: !v?.hasOwnProperty('value') && !v?.length
                        });
                        setResidency(v);
                      }}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      label="country of residency"
                    />
                  </div>
                  <div className="d-flex flex-row cell-with-label">
                    <label className="pr-1">{t('l.rates_request_test.promo_code')}
                    </label>
                    <input type="text" value={promoCode} defaultValue={30} onChange={
                      (v) => setPromoCode(v.target.value)}
                           className="form-control col filter-select pl-1"/>
                  </div>
                </div>
                <div className="d-flex flex-row justify-content-end pt-1">
                  <button type="submit" className="btn btn-primary filter-button" onClick={makeRequest}>
                    {t('b.rates_request_test.make_request').toUpperCase()}
                  </button>
                </div>
                <div className="d-flex justify-content-end flex-grow-1 align-items-end">
                  <div className="link-item" onClick={openXMLInfoLink}>
                    {t('b.rates_request_test.xlm_info_link_label')}
                  </div>

                </div>
              </div>
            </div>
          </div>
        </form>
        <div className="scrolling-modal-div-large pt-1">
          <XMLViewer xml={responseXMLData} collapsible={true}/>
        </div>
        {loading &&
            <Spinner animation="border" variant="info" className="mt-1"/>
        }
        {failedRequest &&
            <span className="text-danger">{t('text.rates_request_test.failure_fetching_rates')}
                        </span>
        }
      </div>
    </main>
  );
};
